/**
 * Il confronto fra un aggiornamento e la ricerca originale (F-140).
 *
 * ## L'ordine è quello delle domande
 *
 *  1. **La lettura** del modello, se c'è: una frase e un paragrafo di andamento.
 *  2. **Gli eletti di allora**: sono ancora candidati? È il fatto più
 *     importante del confronto, e sta in alto.
 *  3. **Rimasti**, con rango, punteggio e prezzo prima → dopo.
 *  4. **Nuovi** e **spariti**.
 *
 * ⚠️ I numeri vengono dal codice e si mostrano anche quando il modello non ha
 * risposto (`degraded`): la parte deterministica **è** il confronto, il testo è
 * un commento. Una variazione di prezzo non calcolabile — valute diverse fra le
 * due fotografie — si mostra con il suo motivo, non con un trattino muto.
 */

import { Link } from 'react-router-dom';
import { formatDate, formatPercent } from '@/lib/format';
import { Alert, Badge, Card, Spinner, TableWrap } from '@/ui';
import { useRunComparison, type ComparisonChanged, type ComparisonRetained } from './api';

function Name({ row }: { row: { displayName: string; symbol: string | null; instrumentId: string | null } }) {
  const testo = row.symbol ? `${row.symbol} — ${row.displayName}` : row.displayName;
  return row.instrumentId ? <Link to={`/instruments/${row.instrumentId}`}>{testo}</Link> : <>{testo}</>;
}

function PriceChange({ row }: { row: ComparisonRetained }) {
  if (row.priceChangePct === null) {
    return (
      <span className="cell--minor" title={row.priceChangeNote ?? undefined}>
        n/d
      </span>
    );
  }
  const negative = row.priceChangePct.startsWith('-');
  return <span className={negative ? 'text-negative' : 'text-positive'}>{formatPercent(row.priceChangePct)}</span>;
}

function ChangedTable({ rows, empty }: { rows: ComparisonChanged[]; empty: string }) {
  if (rows.length === 0) return <p className="cell--minor">{empty}</p>;
  return (
    <TableWrap>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>Titolo</th>
            <th className="numeric">Rango</th>
            <th className="numeric">Punteggio</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.displayName}:${row.rank}`}>
              <td>
                <Name row={row} />
                {row.wasPicked && (
                  <>
                    {' '}
                    <Badge tone="warning">era eletto</Badge>
                  </>
                )}
              </td>
              <td className="numeric">{row.rank}</td>
              <td className="numeric">{row.score ?? <span className="cell--minor">n/d</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export function ComparisonCard({ runId, parentRunId, parentCreatedAt }: { runId: string; parentRunId: string; parentCreatedAt: string }) {
  const { data, isPending } = useRunComparison(runId, true);
  const comparison = data?.comparison ?? null;

  if (isPending) return <Spinner label="Carico il confronto…" />;

  if (!comparison || comparison.status === 'queued' || comparison.status === 'running') {
    return (
      <Card title="Confronto con la ricerca precedente">
        <Spinner label="Confronto in preparazione: i numeri sono pronti in pochi secondi, la lettura del modello in qualche decina." />
      </Card>
    );
  }

  if (comparison.status === 'failed' || !comparison.stats) {
    return (
      <Card title="Confronto con la ricerca precedente">
        <Alert variant="error" title="Confronto non riuscito">
          {comparison.errorMessage ?? 'Errore non specificato.'}
        </Alert>
      </Card>
    );
  }

  const { stats, summary } = comparison;

  return (
    <Card
      title="Cosa è cambiato dalla ricerca precedente"
      subtitle={
        <>
          Rispetto alla <Link to={`/discovery/${parentRunId}`}>ricerca del {formatDate(parentCreatedAt)}</Link>,{' '}
          <strong>{stats.elapsedDays} giorni</strong> dopo · {stats.retained.length} rimasti, {stats.added.length}{' '}
          nuovi, {stats.dropped.length} spariti
        </>
      }
    >
      {/* ⚠️ Degradato = numeri senza commento, esito valido e dichiarato. Non si
          nasconde il riquadro: la parte che vale di piu' c'e'. */}
      {comparison.status === 'degraded' && (
        <Alert variant="warning" title="La lettura del modello non è arrivata">
          I numeri qui sotto sono completi; manca solo il commento.
          {comparison.errorMessage && <> Motivo: {comparison.errorMessage}</>}
        </Alert>
      )}

      {summary && (
        <div className="comparison__reading">
          <p className="comparison__headline">{summary.headline}</p>
          <p>{summary.marketReading}</p>
          {summary.notableChanges.length > 0 && (
            <ul className="comparison__notes">
              {summary.notableChanges.map((nota) => (
                <li key={nota}>{nota}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Il fatto piu' importante, in alto: gli eletti di allora sono ancora qui? */}
      {stats.pickedTotal > 0 && (
        <p className="comparison__picked">
          Degli <strong>{stats.pickedTotal}</strong> titoli eletti dalla rotazione precedente,{' '}
          <strong>{stats.pickedStillPresent}</strong> {stats.pickedStillPresent === 1 ? 'è' : 'sono'} ancora fra i
          candidati.
        </p>
      )}

      <h3 className="comparison__section">Rimasti ({stats.retained.length})</h3>
      {stats.retained.length === 0 ? (
        <p className="cell--minor">Nessun titolo della prima ricerca compare nella seconda.</p>
      ) : (
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Titolo</th>
                <th className="numeric">Rango</th>
                <th className="numeric">Punteggio</th>
                <th className="numeric col--secondary">Prezzo</th>
                <th className="numeric">Da allora</th>
              </tr>
            </thead>
            <tbody>
              {stats.retained.map((row) => (
                <tr key={`${row.displayName}:${row.rankAfter}`}>
                  <td>
                    <Name row={row} />
                    {row.wasPicked && (
                      <>
                        {' '}
                        <Badge tone="warning">era eletto</Badge>
                      </>
                    )}
                    {row.matchedBy === 'name' && (
                      <div className="cell--minor" title="Riconosciuto per nome e non per strumento: è un'ipotesi">
                        riconosciuto per nome
                      </div>
                    )}
                  </td>
                  <td className="numeric">
                    {row.rankBefore} → {row.rankAfter}
                  </td>
                  <td className="numeric">
                    {row.scoreBefore ?? 'n/d'} → {row.scoreAfter ?? 'n/d'}
                  </td>
                  <td className="numeric col--secondary">
                    {row.priceBefore ?? 'n/d'} → {row.priceAfter ?? 'n/d'} {row.currency ?? ''}
                  </td>
                  <td className="numeric">
                    <PriceChange row={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {summary && summary.retainedNotes.length > 0 && (
        <ul className="comparison__notes">
          {summary.retainedNotes.map((nota) => (
            <li key={nota}>{nota}</li>
          ))}
        </ul>
      )}

      <h3 className="comparison__section">Nuovi ({stats.added.length})</h3>
      <ChangedTable rows={stats.added} empty="Nessun titolo nuovo: la seconda ricerca ha trovato solo titoli già noti." />

      <h3 className="comparison__section">Spariti ({stats.dropped.length})</h3>
      <ChangedTable rows={stats.dropped} empty="Nessun titolo è sparito." />

      <p className="cell--minor comparison__foot">
        I numeri sono calcolati dal sistema sulle due fotografie; il commento è del modello
        {comparison.llmModel && ` (${comparison.llmModel})`}. Non è consulenza: è lettura dei dati.
      </p>
    </Card>
  );
}
